'use client'

import { useAuth } from '@repo/auth'
import { type AccessSubject, canUseGodMode as canUseGodModeCapability } from '@repo/permissions'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

type GodModeContextValue = {
  /**
   * Whether the current user is allowed to use god mode at all.
   */
  canUseGodMode: boolean
  /**
   * Turns god mode off. Safe to call even when already off.
   */
  disableGodMode: () => void
  /**
   * Whether god mode is currently enabled for this browser session.
   * Defaults to false and resets whenever the authenticated user changes.
   */
  isGodModeEnabled: boolean
  /**
   * Toggles god mode on/off.
   */
  toggleGodMode: () => void
}

const GodModeContext = createContext<GodModeContextValue | null>(null)

type CurrentUserSubject = AccessSubject & { id?: number }

export const GodModeProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  const [isGodModeEnabled, setIsGodModeEnabled] = useState(false)
  const [sessionUserId, setSessionUserId] = useState(user?.id)

  const subject: CurrentUserSubject | null = user
  const canUseGodMode = canUseGodModeCapability(subject)
  const godModeActive = canUseGodMode && isGodModeEnabled

  // Reset during render when the user changes or loses access.
  if (sessionUserId !== user?.id || (isGodModeEnabled && !canUseGodMode)) {
    setSessionUserId(user?.id)
    setIsGodModeEnabled(false)
  }

  useEffect(() => {
    const root = document.documentElement

    if (godModeActive) {
      root.dataset.godMode = 'active'
    } else {
      delete root.dataset.godMode
    }

    return () => {
      delete root.dataset.godMode
    }
  }, [godModeActive])

  const toggleGodMode = () => {
    setIsGodModeEnabled(enabled => (canUseGodMode ? !enabled : false))
  }

  return (
    <GodModeContext.Provider
      value={{
        canUseGodMode,
        disableGodMode: () => setIsGodModeEnabled(false),
        isGodModeEnabled,
        toggleGodMode,
      }}
    data-sentry-element="GodModeContext.Provider" data-sentry-component="GodModeProvider" data-sentry-source-file="god-mode-provider.tsx">
      <output
        aria-atomic="true"
        aria-label="God mode status"
        aria-live="polite"
        className="sr-only"
      >
        {godModeActive ? 'God mode enabled' : 'God mode disabled'}
      </output>
      {children}
    </GodModeContext.Provider>
  )
}

export const useGodMode = () => {
  const context = useContext(GodModeContext)
  if (!context) {
    throw new Error('useGodMode must be used within GodModeProvider')
  }
  return context
}
